import type { XmaxTelemetryEventName, XmaxTelemetryParams } from './collector'

const MAX_ASSETS = 50
const MAX_EVENTS_PER_ASSET = 6
const ASSET_TTL_MS = 60 * 60 * 1000

const ASSET_EVENT_NAMES = new Set<XmaxTelemetryEventName>([
  'sdk_asset_sts_request',
  'sdk_asset_sts_response',
  'sdk_asset_upload_start',
  'sdk_asset_upload_end',
])

export type UploadedAssetEvent = {
  name: XmaxTelemetryEventName
  params: XmaxTelemetryParams
  monotonicAtMs: number
  epochAtMs: number
}

export type UploadedAssetTrace = {
  operationId: string
  createdAtMonotonicMs: number
  completedAtMonotonicMs: number
  events: UploadedAssetEvent[]
}

type AssetReplayTrace = {
  startedAt: number
  record: (name: XmaxTelemetryEventName, params: XmaxTelemetryParams) => void
}

const uploadedAssets = new Map<string, UploadedAssetTrace>()
const assetOperations = new Map<string, UploadedAssetTrace>()

function removeOperation(operationId: string): void {
  assetOperations.delete(operationId)
  uploadedAssets.forEach((value, key) => {
    if (value.operationId === operationId) uploadedAssets.delete(key)
  })
}

function pruneAssets(now: number): void {
  assetOperations.forEach((value) => {
    if (now - (value.completedAtMonotonicMs || value.createdAtMonotonicMs) > ASSET_TTL_MS) {
      removeOperation(value.operationId)
    }
  })
}

function assetOperation(operationId: string): UploadedAssetTrace {
  const now = performance.now()
  pruneAssets(now)
  let value = assetOperations.get(operationId)
  if (!value) {
    while (assetOperations.size >= MAX_ASSETS) {
      const oldest = assetOperations.keys().next().value
      if (typeof oldest !== 'string') break
      removeOperation(oldest)
    }
    value = { operationId, createdAtMonotonicMs: now, completedAtMonotonicMs: 0, events: [] }
    assetOperations.set(operationId, value)
  }
  return value
}

export function recordUploadedAssetEvent(
  operationId: string,
  name: XmaxTelemetryEventName,
  params: XmaxTelemetryParams,
): void {
  if (!ASSET_EVENT_NAMES.has(name)) return
  const value = assetOperation(operationId)
  if (value.events.length >= MAX_EVENTS_PER_ASSET) return
  value.events.push({
    name,
    params: { ...params },
    monotonicAtMs: performance.now(),
    epochAtMs: Date.now(),
  })
}

/** Keep a bounded in-memory association. Asset URLs are never emitted. */
export function registerUploadedAsset(url: string, operationId: string): void {
  const now = performance.now()
  const value = assetOperation(operationId)
  value.completedAtMonotonicMs = now
  while (uploadedAssets.size >= MAX_ASSETS && !uploadedAssets.has(url)) {
    const oldest = uploadedAssets.keys().next().value
    if (typeof oldest !== 'string') break
    uploadedAssets.delete(oldest)
  }
  uploadedAssets.set(url, value)
}

export function findUploadedAsset(url: string | null | undefined): UploadedAssetTrace | undefined {
  if (!url) return undefined
  const value = uploadedAssets.get(url)
  if (!value) return undefined
  if (performance.now() - (value.completedAtMonotonicMs || value.createdAtMonotonicMs) > ASSET_TTL_MS) {
    removeOperation(value.operationId)
    return undefined
  }
  return value
}

export function replayUploadedAssetEvents(trace: AssetReplayTrace, asset: UploadedAssetTrace): void {
  for (const event of asset.events) {
    const {
      trace_id: _traceId,
      call_id: _callId,
      session_id: _sessionId,
      generation_id: _generationId,
      trace_start_time_ms: _traceStartTime,
      event_id: _eventId,
      event_seq: _eventSeq,
      event_time_ms: _eventTime,
      offset_ms: _offset,
      ...params
    } = event.params
    trace.record(event.name, {
      ...params,
      operation_id: asset.operationId,
      event_time_ms: event.epochAtMs,
      offset_ms: Math.min(-1, Math.round(event.monotonicAtMs - trace.startedAt)),
    })
  }
}
